<script setup lang="ts">
/* =============================================================
   BOOKING — DETAIL
   =============================================================
   Everything about one booking, and the only actions a booking
   has: confirm or reject a request (what the berth owner would
   do), and cancel before arrival (what the booker would do).
   Staff may do both -- the database functions allow it, which is
   what makes the demo drivable from here.

   No edit and no delete. A booking's times and price are a
   record of what was agreed; changing them is a new booking.
   ============================================================= */

import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import Badge from '@/components/ui/Badge.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import DetailList from '@/components/ui/DetailList.vue'
import type { DetailRow } from '@/components/ui/DetailList.vue'
import { routes, to } from '@/config/routes'
import { cancelBooking, getBooking, respondToBooking } from '@/lib/bookings'
import { listRates } from '@/lib/rates'
import { useAmenities } from '@/lib/amenities'
import {
  bookingModeLabel,
  bookingState,
  bookingStateLabel,
  bookingStateTone,
  formatDateTime,
  formatDuration,
  formatEur,
  rateConditionText,
  ratePriceText,
} from '@/lib/bookingLabels'
import { errorMessage } from '@/lib/errors'
import type { BerthRate, Booking } from '@/lib/types'

const route = useRoute()
const { amenityLabel } = useAmenities()

const booking = ref<Booking | null>(null)
const rate = ref<BerthRate | null>(null)
const loading = ref(true)
const loadError = ref('')
const actionError = ref('')

const id = computed(() => Number(route.params.id))

async function load(): Promise<void> {
  loading.value = true
  loadError.value = ''
  try {
    booking.value = await getBooking(id.value)
    // The rule that set the price, to say why it is what it is. Fetched from
    // the berth's card rather than embedded, because the rule may since have
    // moved or been deleted -- rate_id is then null and nothing is shown.
    const b = booking.value
    rate.value = b?.rate_id ? ((await listRates(b.berth_id)).find((r) => r.id === b.rate_id) ?? null) : null
  } catch (e) {
    loadError.value = errorMessage(e, 'Could not load the booking.')
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(id, load)

const state = computed(() => (booking.value ? bookingState(booking.value) : null))

const rows = computed<DetailRow[]>(() => {
  const b = booking.value
  if (!b) return []
  return [
    b.berth
      ? { label: 'Berth', value: `${b.berth.name}, ${b.berth.city}`, to: to.berth(b.berth.id) }
      : { label: 'Berth', value: String(b.berth_id), mono: true },
    b.vessel
      ? { label: 'Vessel', value: `${b.vessel.name} · ${b.vessel.length_m} m`, to: to.vessel(b.vessel.id) }
      : { label: 'Vessel', value: String(b.vessel_id), mono: true },
    b.company
      ? { label: 'Booked by', value: b.company.name, to: to.company(b.company.id) }
      : { label: 'Booked by', value: String(b.company_id), mono: true },
    { label: 'Arrival', value: formatDateTime(b.arrival) },
    { label: 'Departure', value: formatDateTime(b.departure) },
    { label: 'Stay', value: formatDuration(b.arrival, b.departure), mono: true },
    { label: 'Price', value: `${formatEur(b.price_eur)} (net of VAT)`, mono: true },
    rate.value
      ? { label: 'Priced by', value: `${rateConditionText(rate.value)} → ${ratePriceText(rate.value)}` }
      : { label: 'Priced by', empty: true },
    b.requested_amenities.length > 0
      ? { label: 'Requested amenities', tags: b.requested_amenities.map(amenityLabel) }
      : { label: 'Requested amenities', empty: true },
    b.berth
      ? { label: 'Berth booking mode', value: bookingModeLabel(b.berth.booking_mode) }
      : { label: 'Berth booking mode', empty: true },
    { label: 'Requested', value: formatDateTime(b.created_at) },
    b.decided_at
      ? { label: b.status === 'rejected' ? 'Rejected' : 'Confirmed', value: formatDateTime(b.decided_at) }
      : { label: 'Decided', empty: true },
    ...(b.cancelled_at ? [{ label: 'Cancelled', value: formatDateTime(b.cancelled_at) }] : []),
    { label: 'ID', value: String(b.id), mono: true },
  ]
})

const canRespond = computed(() => booking.value?.status === 'requested')
const canCancel = computed(() => {
  const b = booking.value
  if (!b) return false
  return (b.status === 'requested' || b.status === 'confirmed') && new Date(b.arrival).getTime() > Date.now()
})

/* --- actions ------------------------------------------------------------- */

const acting = ref<'' | 'confirm' | 'reject'>('')

async function respond(accept: boolean): Promise<void> {
  const b = booking.value
  if (!b || acting.value) return
  acting.value = accept ? 'confirm' : 'reject'
  actionError.value = ''
  try {
    await respondToBooking(b.id, accept)
    await load()
  } catch (e) {
    actionError.value = errorMessage(e, 'Could not update the booking.')
  } finally {
    acting.value = ''
  }
}

const cancelling = ref(false)
const confirm = useTemplateRef<InstanceType<typeof ConfirmDialog>>('confirm')

async function onCancelConfirmed(): Promise<void> {
  const b = booking.value
  if (!b) return
  try {
    await cancelBooking(b.id)
    cancelling.value = false
    await load()
  } catch (e) {
    confirm.value?.fail(errorMessage(e, 'Could not cancel the booking.'))
  }
}
</script>

<template>
  <p v-if="loading && !booking" class="page-status">Loading…</p>

  <template v-else-if="booking && state">
    <PageHeader
      :title="booking.reference"
      :description="booking.berth ? `${booking.berth.name}, ${booking.berth.city}` : undefined"
    >
      <template #actions>
        <Button variant="ghost" icon="chevronRight" :to="routes.bookings" class="back-link">
          All bookings
        </Button>
        <template v-if="canRespond">
          <Button
            variant="secondary"
            icon="close"
            :loading="acting === 'reject'"
            :disabled="acting !== ''"
            @click="respond(false)"
          >
            Reject
          </Button>
          <Button icon="check" :loading="acting === 'confirm'" :disabled="acting !== ''" @click="respond(true)">
            Confirm
          </Button>
        </template>
        <Button v-if="canCancel" variant="danger" icon="trash" @click="cancelling = true">Cancel booking</Button>
      </template>
    </PageHeader>

    <Alert v-if="actionError" tone="danger" class="page-alert">{{ actionError }}</Alert>

    <Alert v-if="state === 'requested'" tone="warning" class="page-alert">
      Waiting for the berth owner. The slot is held until it is confirmed, rejected or cancelled.
    </Alert>

    <Panel>
      <template #actions>
        <Badge :tone="bookingStateTone(state)">{{ bookingStateLabel(state) }}</Badge>
      </template>
      <DetailList :rows="rows" />
    </Panel>

    <ConfirmDialog
      ref="confirm"
      :open="cancelling"
      title="Cancel booking"
      :message="`${booking.reference} will be cancelled and the slot freed. No cancellation fee is charged in the demo.`"
      confirm-label="Cancel booking"
      @close="cancelling = false"
      @confirmed="onCancelConfirmed"
    />
  </template>

  <template v-else>
    <Alert v-if="loadError" tone="danger" class="page-alert">{{ loadError }}</Alert>
    <EmptyState
      v-else
      icon="bookings"
      title="Booking not found"
      description="The link may be wrong."
    >
      <template #action>
        <Button :to="routes.bookings">All bookings</Button>
      </template>
    </EmptyState>
  </template>
</template>
